import React, { useState, useEffect } from 'react';
import { Routes, Route, HashRouter } from 'react-router-dom';
import { 
  Box, 
  Flex, 
  ChakraProvider, 
  FormControl, 
  FormLabel, 
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Text,
  Container,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Link
} from '@chakra-ui/react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import Dashboard from './components/Dashboard';
import TeamDetail from './components/TeamDetail';
import RaceDetail from './components/RaceDetail';
import RacerDetail from './components/RacerDetail';
import CategoryView from './components/CategoryView';
import Statistics from './components/Statistics';
import SideMenu from './components/SideMenu';
import Races from './components/Races';
import Racers from './components/Racers';
import Teams from './components/Teams';
import { supabase } from './supabaseClient';
import { ScrollToTop } from './components/ScrollToTop';
import { Helmet } from 'react-helmet-async';
import Settings from './pages/Settings';
import ResponsiveNavigation from './components/ResponsiveNavigation';
import type { Database } from './types/supabase';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

// Add these interfaces at the top of the file
interface TeamData {
  team: string;
  members: Set<string>;
  positionCounts: Record<string, number>;
  totalRaces: number;
}

interface TeamSummary {
  team: string;
  memberCount: number;
  positionCounts: Record<string, number>;
  members: string[];
  totalRaces: number;
}

function App() {
  const [opacity, setOpacity] = useState(() => {
    const saved = localStorage.getItem('backgroundOpacity');
    return saved !== null ? JSON.parse(saved) : 0.5;
  });

  const [teamStats, setTeamStats] = useState<TeamSummary[]>([]);
  const [teamStatsLoading, setTeamStatsLoading] = useState(true);
  const [supabaseError, setSupabaseError] = useState<string | null>(null);
  
  useEffect(() => {
    localStorage.setItem('backgroundOpacity', JSON.stringify(opacity));
  }, [opacity]);

  useEffect(() => {
    const fetchTeamStats = async () => {
      const { data, error } = await supabase
        .from('medvedica_race_stats')
        .select('*')
        .not('team', 'is', null);

      if (error) {
        console.error('Error fetching team stats:', error);
        setSupabaseError('Unable to load data from database. Some statistics may be unavailable.');
        setTeamStatsLoading(false);
        return;
      }

      // Explicitly type races to avoid `never` inference issues from Supabase typings
      const races = (data ?? []) as Database['public']['Tables']['medvedica_race_stats']['Row'][];

      // Process the data to create team statistics
      const teamData: Record<string, TeamData> = {};

      races.forEach(race => {
        if (!race.team) return;

        if (!teamData[race.team]) {
          teamData[race.team] = {
            team: race.team,
            members: new Set(),
            positionCounts: {},
            totalRaces: 0
          };
        }

        teamData[race.team].members.add(race.name);
        teamData[race.team].totalRaces += 1; // Count each race entry
        
        const position = race.position; // Already a number in Supabase typings
        if (!isNaN(position)) {
          teamData[race.team].positionCounts[position] = 
            (teamData[race.team].positionCounts[position] || 0) + 1;
        }
      });

      // Fetch more accurate data from the frontend_racers_summary_mv (materialized view)
      const { data: racersData, error: racersError } = await supabase
        .from('frontend_racers_summary_mv')
        .select('*');

      if (!racersError && racersData) {
        // Explicitly type racersData as a generic array to avoid `never` inference issues
        const typedRacersData = racersData as any[];

        // Group racers by team (we only rely on gold_* fields here)
        const racersByTeam: Record<string, any[]> = {};
        
        typedRacersData.forEach(racer => {
          if (!racer.gold_team) return;
          
          if (!racersByTeam[racer.gold_team]) {
            racersByTeam[racer.gold_team] = [];
          }
          
          racersByTeam[racer.gold_team].push(racer);
        });
        
        // Update team data with more accurate information
        Object.keys(racersByTeam).forEach(teamName => {
          if (!teamData[teamName]) {
            teamData[teamName] = {
              team: teamName,
              members: new Set(),
              positionCounts: {},
              totalRaces: 0
            };
          }
          
          // Update members from the racers view
          teamData[teamName].members = new Set(
            racersByTeam[teamName].map(racer => racer.gold_name)
          );
          
          // Calculate total races from individual racer race counts
          teamData[teamName].totalRaces = racersByTeam[teamName].reduce(
            (sum, racer) => sum + racer.num_races, 0
          );
        });
      } else if (racersError) {
        // 57014 = statement timeout: view query too slow; fix by increasing statement_timeout in Supabase or using a materialized view
        const isTimeout = racersError.code === '57014';
        console.error('Error fetching racers summary:', racersError.message, racersError.code, racersError.details);
        if (isTimeout) {
          setSupabaseError('Racer summary request timed out. The view query is too slow for the server timeout. Try again later; to fix permanently, increase statement_timeout in Supabase or switch to a materialized view.');
        } else {
          setSupabaseError('Unable to load racer summary data. Some team information may be incomplete.');
        }
      }

      // Convert to array format
      const processedTeamStats = Object.values(teamData).map(data => ({
        team: data.team,
        memberCount: data.members.size,
        positionCounts: data.positionCounts,
        members: Array.from(data.members),
        totalRaces: data.totalRaces
      }));

      setTeamStats(processedTeamStats);
      setTeamStatsLoading(false);
    };

    fetchTeamStats();
  }, []);

  return (
    <HashRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <ChakraProvider>
        <Helmet>
          <title>Medvedica SKI PRO Team</title>
          <meta name="description" content="Track and analyze ski race statistics for Medvedica SKI PRO Team" />
          <meta name="keywords" content="ski, racing, Medvedica, statistics, team performance" />
          <meta property="og:title" content="Medvedica SKI PRO Team" />
          <meta property="og:description" content="Professional ski racing statistics and analysis" />
          <meta property="og:image" content="/img/Silhouetted.jpg" />
          <link rel="canonical" href="https://your-domain.com" />
        </Helmet>
        <Box 
          minH="100vh" 
          w="100%" 
          bgImage="/img/Silhouetted.jpg"
          bgPosition="center"
          bgRepeat="no-repeat"
          bgSize="cover"
          bgAttachment="fixed"
          position="relative"
        >
          <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            bg="rgba(255, 255, 255, 0.92)"
            zIndex="0"
            sx={{
              filter: "grayscale(100%)"
            }}
          />
          
          <Box position="relative" zIndex="1" pt={0}>
            <ResponsiveNavigation />
            <Box 
              w="100%" 
              // Use left padding instead of margin so content width never exceeds viewport,
              // which avoids horizontal scrollbars on desktop while keeping space for the sidebar
              pl={{ base: 0, md: "70px" }} 
              transition="padding 0.3s"
              pt={0}
            >
              {/* DB error alert: sticky so visible on both mobile and desktop when present.
                  If it only appeared on mobile before, the fetch likely failed there (e.g. network)
                  while succeeding on desktop (e.g. cache). */}
              {supabaseError && (
                <Box
                  px={4}
                  pt={4}
                  pb={2}
                  position="sticky"
                  top={0}
                  zIndex={5}
                  bg="white"
                  borderBottomWidth="1px"
                  borderColor="red.200"
                >
                  <Alert status="error" variant="subtle" borderRadius="md" boxShadow="sm">
                    <AlertIcon />
                    <Box>
                      <AlertTitle fontSize="sm">
                        {supabaseError.includes('timed out') ? 'Request timed out' : 'Database connection issue'}
                      </AlertTitle>
                      <AlertDescription fontSize="sm">
                        {supabaseError}
                      </AlertDescription>
                    </Box>
                  </Alert>
                </Box>
              )}
              <ScrollToTop>
                <Routes>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/races" element={<Races />} />
                  <Route path="/racers" element={<Racers />} />
                  <Route path="/team" element={<Teams teamStats={teamStats} isLoading={teamStatsLoading} />} />
                  <Route path="/teamDetail/:team" element={<TeamDetail />} />
                  <Route path="/race/:race_code" element={<RaceDetail />} />
                  <Route path="/racer/:name" element={<RacerDetail />} />
                  <Route path="/category/:category" element={<CategoryView />} />
                  <Route path="/statistics" element={<Statistics />} />
                  <Route path="/settings" element={<Settings />} />
                </Routes>
              </ScrollToTop>
            </Box>
          </Box>
          <Footer />
        </Box>
      </ChakraProvider>
    </HashRouter>
  );
}

// Create a new Footer component
const Footer = () => (
  <Box
    as="footer"
    py={{ base: 2, md: 4 }}
    mt="auto"
    // Align footer content with main window (to the right of the sidebar)
    pl={{ base: 0, md: "70px" }}
    position="relative"
    zIndex={1}
  >
    <Container 
      // Make footer adapt to small screens while staying nicely centered on desktop
      maxW={{ base: '100%', md: 'container.xl' }}
      bg="white"
      borderRadius={{ base: 'md', md: 'full' }}
      py={{ base: 2, md: 3 }}
      px={{ base: 4, md: 8 }}
      boxShadow="sm"
      border="1px solid"
      borderColor="gray.200"
      mx="auto"
      mb={{ base: 2, md: 4 }}
    >
      <Box textAlign="center">
        <Text 
          color="blackAlpha.800"
          // Slightly smaller on desktop, extra compact on mobile to ensure it always fits
          fontSize={{ base: 'xs', md: 'sm' }}
          fontWeight="medium"
        >
          Ski Stat - Created for MEDVEDiCA SKI PRO Team &copy; 2025-26 by Ivan Bukovcan <Text as="span" color="blue.500">skistat@buko.sk</Text>
        </Text>
        {/* Very small font: openstatus ref to status page */}
        <Text fontSize="xs" color="gray.500" mt={1}>
          <Link href="https://skistat-buko.openstatus.dev/" isExternal color="blue.500">
            openstatus
          </Link>
        </Text>
      </Box>
    </Container>
  </Box>
);

export default App;
